<template>
  <div class="ra-actions">
    <v-tooltip bottom v-if="canAddAsTemplate && canManageStructureHere">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="addAsTemplate(item)"
          :loading="addingTemplate"
          data-cy="addToTemplate"
        >
          mdi-file-plus
        </v-icon>
      </template>
      <span>{{ $t("riskAssessment.addToTemplate") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canReview">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="openReviewDialog"
          data-cy="proof"
        >
          mdi-check-decagram
        </v-icon>
      </template>
      <span>{{ $t("riskAssessment.proof.reviewButton") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="openMoveDialog(item)"
          data-cy="move"
        >
          content_paste_go
        </v-icon>
      </template>
      <span>{{ $t("riskAssessment.move") }}</span>
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="isUploaded ? downloadDocument(item) : openPrintPdf(item)"
          data-cy="open"
          >picture_as_pdf</v-icon
        >
      </template>
      <span>{{
        isUploaded
          ? $t("operatingInstructions.download")
          : $t("riskAssessment.openRiskAssessment")
      }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canCreateHere && !isUploaded">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="copyItem(item)"
          data-cy="copy"
          >content_copy</v-icon
        >
      </template>
      <span>{{ $t("riskAssessment.copyRiskAssessment") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="editItem(item)"
          data-cy="edit"
        >
          edit</v-icon
        >
      </template>
      <span>{{ $t("riskAssessment.editRiskAssessment") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canDeleteHere">
      <template v-slot:activator="{ on }">
        <v-icon
          medium
          class="mr-2"
          v-on="on"
          @click.stop="openDeleteDialog(item)"
          data-cy="delete"
        >
          delete</v-icon
        >
      </template>
      <span>{{ $t("riskAssessment.deleteRiskAssessment") }}</span>
    </v-tooltip>
    <riskAssessmentUploadDialog
      v-if="isUploaded"
      v-model="uploadDialogOpen"
      :item="item"
      @saved="reloadDataFunc"
    />
    <deleteDialog
      @triggerDelete="deleteItem"
      :item="item"
      :deleteDialog="deleteDialog"
    />
    <!-- Loading Dialog -->
    <v-dialog v-model="loadPdfDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t("riskAssessment.createDokument") }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <moveDialog
      :item="item"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="riskAssessmentCategories"
      @cancel="moveDialog.open = false"
      @reload="reloadDataFunc"
      @confirm="handleMoveConfirm"
    />
    <v-dialog v-model="reviewDialog.open" max-width="540" persistent>
      <v-card>
        <v-card-title>{{
          $t("riskAssessment.proof.reviewTitle")
        }}</v-card-title>
        <v-card-subtitle class="pb-0">{{ item.title }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <v-radio-group v-model="mode" hide-details class="mt-0">
            <v-radio v-if="canProof" value="approve" color="primary">
              <template v-slot:label>
                <span>{{
                  isUploaded
                    ? $t("riskAssessment.proof.agreeUploaded")
                    : $t("riskAssessment.proof.agree")
                }}</span>
              </template>
            </v-radio>
            <v-radio value="reject" color="error" class="mt-3">
              <template v-slot:label>
                <span>{{ $t("riskAssessment.proof.disagree") }}</span>
              </template>
            </v-radio>
          </v-radio-group>

          <v-textarea
            v-if="mode === 'reject'"
            v-model="rejectReason"
            :label="$t('riskAssessment.proof.rejectReason')"
            class="mt-4"
            outlined
            dense
            rows="3"
            auto-grow
            hide-details
            autofocus
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />

          <v-btn text @click="closeReview" :disabled="busy">
            {{ $t("general.cancel") }}
          </v-btn>

          <v-btn
            :color="mode === 'reject' ? 'error' : 'primary'"
            :disabled="!canConfirm"
            :loading="busy"
            @click="confirmReview"
          >
            {{ $t("general.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import deleteDialog from "../../_utils/dataTableHelpers/actions/deleteDialog.vue";
import { openPdfFromBase64 } from "../../../utils/openPdf";
import moveDialog from "../../_utils/dataTableHelpers/actions/moveDialog.vue";
import riskAssessmentUploadDialog from "../../../views/riskAssessment/riskAssessmentUploadDialog.vue";
export default {
  props: ["item", "config"],
  components: {
    deleteDialog,
    moveDialog,
    riskAssessmentUploadDialog,
  },
  data: () => ({
    deleteDialog: {
      open: false,
      title: "riskAssessment.deleteTitle",
      description: "",
    },
    moveDialog: {
      open: false,
      title: "riskAssessment.move",
      description: "",
      targetId: null,
    },
    loadPdfDialog: false,
    uploadDialogOpen: false,
    moving: false,
    addingTemplate: false,
    addedAsTemplate: false,
    riskAssessmentCategories: [],
    reviewDialog: { open: false },
    mode: null,
    proofing: false,
    rejectReason: "",
    rejecting: false,
  }),
  computed: {
    isUploaded() {
      return this.item && this.item.source === "uploaded";
    },
    currentVersionData() {
      const versions = (this.item && this.item.versions) || [];
      if (!versions.length) return null;
      return (
        versions.find((v) => v.version === this.item.currentVersion) ||
        versions[versions.length - 1]
      );
    },
    //Only a member of the stage the chain is currently waiting on may approve.
    canProof() {
      const uid = this.config && this.config.currentUserId;
      if (!uid) return false;

      const version = this.currentVersionData;
      if (!version || version.status !== "pending") return false;

      const stage = (version.stages || [])[version.currentStage || 0];
      if (!stage || stage.approvedAt) return false;

      return (stage.members || []).some((m) => String(m.user) === String(uid));
    },
    busy() {
      return this.proofing || this.rejecting;
    },
    canConfirm() {
      if (this.mode === "approve") return true;
      if (this.mode === "reject") return !!this.rejectReason.trim();
      return false;
    },
    //One icon opens the review dialog: it is shown to anyone who may act,
    //whether that is approving the active stage or rejecting the version.
    canReview() {
      return this.canProof || this.canReject;
    },
    //Anyone in the chain may reject while the version is still pending.
    canReject() {
      const uid = this.config && this.config.currentUserId;
      if (!uid) return false;

      const version = this.currentVersionData;
      if (!version || version.status !== "pending") return false;

      return (version.stages || []).some((stage) =>
        (stage.members || []).some((m) => String(m.user) === String(uid))
      );
    },
    canAddAsTemplate() {
      if (this.isUploaded) return false;
      if (!this.config || !this.config.canManageTemplates) return false;
      if (this.addedAsTemplate) return false;
      const id = String(this.item._id || this.item.id);
      const sources = this.config.templateSourceIds || [];
      return !sources.map(String).includes(id);
    },
  },
  methods: {
    async addAsTemplate(item) {
      this.addingTemplate = true;
      try {
        const risks = (item.risks || []).map((r) => ({
          stepTitle: r.stepTitle,
          title: r.title,
          riskClassification: r.riskClassification,
          action: r.action,
          riskClassificationAfterAction: r.riskClassificationAfterAction,
          measurement: r.measurement,
          legal: r.legal,
          relevantForPregnant: r.relevantForPregnant,
          actionForPregnant: r.actionForPregnant,
          riskClassificationAfterActionForPregnant:
            r.riskClassificationAfterActionForPregnant,
          involvesThirdParties: r.involvesThirdParties,
          requiresSpecialTraining: r.requiresSpecialTraining,
          hazardFactor: r.hazardFactor,
          hazardFactors: r.hazardFactors,
          hazardPoints: r.hazardPoints,
          hazardDescription: r.hazardDescription,
          measureType: r.measureType,
          measureTypeText: r.measureTypeText,
          stopMeasures: r.stopMeasures,
          furtherMeasures: r.furtherMeasures,
          furtherMeasuresText: r.furtherMeasuresText,
          riskClassificationAfterFurtherMeasures:
            r.riskClassificationAfterFurtherMeasures,
        }));

        await this.$store.dispatch("createRiskAssessmentTemplate", {
          name: item.title,
          type: "local",
          kind: item.kind === "advanced" ? "advanced" : "basic",
          sourceRiskAssessment: item._id || item.id,
          risks,
        });

        // Hide the action right away and let the table refresh its source ids.
        this.addedAsTemplate = true;
        this.$root.$emit("ra-template-added");
      } finally {
        this.addingTemplate = false;
      }
    },
    editItem(item) {
      if (this.isUploaded) {
        this.uploadDialogOpen = true;
        return;
      }
      this.$router.push({
        name: "riskAssessmentForm",
        params: { id: item.id, mode: "edit" },
      });
    },
    copyItem(item) {
      this.$router.push({
        name: "riskAssessmentForm",
        params: { id: item.id, mode: "copy" },
      });
    },
    openDeleteDialog(item) {
      this.deleteDialog.open = true;
      this.deleteDialog.description = this.$t("riskAssessment.deleteConfirm", {
        name: item.title,
      });
    },
    deleteItem(item) {
      this.$store.dispatch("deleteRiskAssessment", item).then(() => {
        this.reloadDataFunc();
        this.deleteDialog.open = false;
      });
    },
    reloadDataFunc() {
      this.$emit("reloadData");
    },
    openReviewDialog() {
      this.rejectReason = "";
      this.mode = this.canProof ? null : "reject";
      this.reviewDialog.open = true;
    },
    closeReview() {
      this.reviewDialog.open = false;
      this.mode = null;
      this.rejectReason = "";
    },
    confirmReview() {
      if (this.mode === "approve") return this.proofItem();
      if (this.mode === "reject") return this.rejectItem();
    },
    async rejectItem() {
      this.rejecting = true;
      try {
        await this.$store.dispatch("rejectRiskAssessment", {
          id: this.item._id || this.item.id,
          reason: this.rejectReason.trim(),
        });
        this.closeReview();
        this.reloadDataFunc();
      } finally {
        this.rejecting = false;
      }
    },

    async proofItem() {
      this.proofing = true;
      try {
        await this.$store.dispatch("proofRiskAssessment", {
          id: this.item._id || this.item.id,
        });
        this.closeReview();
        this.reloadDataFunc();
      } catch (e) {
        // error snackbar already shown by the store action
      } finally {
        this.proofing = false;
      }
    },
    async downloadDocument(item) {
      const doc = item && item.document;
      if (!doc || !doc.location) return;
      const url = await this.$store.dispatch(
        "getPresignedDownloadUrl",
        `Gefaehrdungsbeurteilung-Dokumente/${doc.name}_${doc.location}.${doc.filetype}`
      );
      if (url) window.open(url, "_blank");
    },
    openPrintPdf(item) {
      let vm = this;
      this.loadPdfDialog = true;
      this.$store.dispatch("getPdfRiskAssessment", item._id).then(function (r) {
        //close loading
        vm.loadPdfDialog = false;
        if (!r || !r.base64) return;
        openPdfFromBase64(r.base64, item.title);
      });
    },

    async openMoveDialog(item) {
      this.moveDialog.open = true;
      this.moveDialog.targetId = item.id;
      this.moveDialog.description = this.$t(
        "riskAssessment.movingRiskAssessment",
        { name: item.name }
      );

      const result = await this.$store.dispatch(
        "getAllRiskAssessmentCategories"
      );

      const allCategories = Array.isArray(result)
        ? result
        : result?.items || result?.data || [];

      const currentId = this.$route.params.riskAssessmentCategoryId;

      this.riskAssessmentCategories = allCategories.filter(
        (loc) => String(loc.id) !== String(currentId)
      );
    },

    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("moveRiskAssessment", {
          id: this.item.id,
          riskAssessmentCategory: targetId,
        });
        this.moveDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.moving = false;
      }
    },
  },
};
</script>

<style scoped>
.ra-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}
.ra-actions .v-icon {
  flex: 0 0 auto;
}
</style>
